// src/components/support/TicketDrawer.jsx
//
// One ticket: progress, conversation + history, and the reply box.
//   scope="client"  progress track, "Yes, it's fixed" / "Still not working"
//   scope="admin"   status / assignee / priority controls, site snapshot,
//                   staff-only notes
// Refreshes every 30 s while open so both sides see replies without reloading.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetchJSON } from '../../api/apiClient';
import Composer from './Composer';
import {
  AttachmentStrip, PRIORITIES, PriorityTag, STATUS_ORDER, TicketStatus,
  formatStamp, statusLabel, timeAgo,
} from './supportShared';

const POLL_MS = 30000;

const TRACK = [
  { id: 'new', label: 'Received' },
  { id: 'assigned', label: 'Assigned' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'closed', label: 'Closed' },
];

function ProgressTrack({ ticket }) {
  const waiting = ticket.status === 'waiting_client';
  const at = waiting ? 2 : TRACK.findIndex((s) => s.id === ticket.status);
  return (
    <ol className="sp-track" aria-label="Ticket progress">
      {TRACK.map((step, i) => {
        const state = i < at ? 'done' : i === at ? 'current' : 'todo';
        const label = i === 2 && waiting ? 'Waiting for you' : step.label;
        return (
          <li key={step.id} className={`sp-step is-${state} ${i === at && waiting ? 'is-waiting' : ''}`}
              aria-current={state === 'current' ? 'step' : undefined}>
            <span className="sp-step-mark" aria-hidden="true" />
            <span className="sp-step-label">{label}</span>
          </li>
        );
      })}
    </ol>
  );
}

function eventText(e, scope) {
  const who = e.actor_type === 'system' ? 'Closed automatically' : e.actor_name || 'Someone';
  switch (e.kind) {
    case 'created':
      return `${who} opened the ticket`;
    case 'assigned':
      return e.to_value ? `${scope === 'admin' ? who + ' assigned it to' : 'Assigned to'} ${e.to_value}` : `${who} unassigned it`;
    case 'priority':
      return `${who} changed priority to ${e.to_value}`;
    case 'status':
      if (e.actor_type === 'system') return 'Closed automatically after no reply';
      return `${who} set status to ${statusLabel(e.to_value, scope)}`;
    default:
      return e.kind;
  }
}

function Snapshot({ snap }) {
  if (!snap) return null;
  const sl = snap.devices.filter((d) => d.type === 'starlink');
  const pl = snap.devices.filter((d) => d.type === 'peplink');
  return (
    <details className="sp-snapshot" open>
      <summary>Vessel when the ticket was raised <span className="bl-muted">{formatStamp(snap.taken_at)}</span></summary>
      {snap.devices.length === 0 && <p className="bl-muted sp-snap-empty">No devices linked to this vessel.</p>}
      <ul className="sp-snap-list">
        {sl.map((d) => (
          <li key={d.key}>
            <span className={`bl-dot ${d.online === true ? 'ok' : d.online === false ? 'bad' : ''}`} />
            Starlink {d.label || d.key}
            <span className="bl-muted"> · {d.online === true ? 'online' : d.online === false ? 'offline' : 'unknown'}
              {d.online_checked_at ? `, checked ${formatStamp(d.online_checked_at)}` : ''}{d.plan ? ` · ${d.plan}` : ''}</span>
          </li>
        ))}
        {pl.map((d) => (
          <li key={d.key}><span className="bl-dot" />Router <span className="ft-mono">{d.label || d.key}</span></li>
        ))}
      </ul>
      {snap.recent_alerts?.length > 0 && (
        <>
          <div className="sp-snap-sub">WAN changes, last 48 h</div>
          <ul className="sp-snap-list">
            {snap.recent_alerts.map((a, i) => (
              <li key={i} className="ft-mono sp-snap-alert">
                <span className="bl-muted">{formatStamp(a.time)}</span> {a.device} · {a.wan}: {a.from || '?'} → {a.to || '?'}
              </li>
            ))}
          </ul>
        </>
      )}
    </details>
  );
}

export default function TicketDrawer({ scope, ticketId, staff = [], me, onClose, onChanged }) {
  const base = scope === 'admin' ? '/api/v2/admin/support' : '/api/v2/client/support';
  const [t, setT] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setT(await apiFetchJSON(`${base}/tickets/${ticketId}`));
      setError(null);
    } catch (e) {
      setError(e.status === 404 ? 'This ticket doesn’t exist or isn’t yours.' : e.message);
    }
  }, [base, ticketId]);

  useEffect(() => {
    load();
    const id = setInterval(() => document.visibilityState === 'visible' && load(), POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // quiet: the caller shows the error itself (the composer does).
  const apply = async (url, body, method = 'POST', quiet = false) => {
    setSaving(true);
    setError(null);
    try {
      const out = await apiFetchJSON(url, { method, body: JSON.stringify(body || {}) });
      setT(out);
      onChanged && onChanged(out);
      return out;
    } catch (e) {
      if (!quiet) setError(e.message);
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const adminPatch = (fields) => apply(`${base}/tickets/${ticketId}`, fields, 'PATCH').catch(() => {});

  const send = (payload) => scope === 'admin'
    ? apply(`${base}/tickets/${ticketId}`, payload, 'PATCH', true)
    : apply(`${base}/tickets/${ticketId}/messages`, payload, 'POST', true);

  // Conversation and history, in time order.
  const feed = useMemo(() => {
    if (!t) return [];
    const items = [
      ...t.messages.map((m) => ({ type: 'msg', at: m.created_at, key: `m${m.id}`, m })),
      ...t.events.filter((e) => e.kind !== 'created').map((e, i) => ({ type: 'evt', at: e.created_at, key: `e${i}`, e })),
    ];
    return items.sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : a.type === 'msg' ? -1 : 1));
  }, [t]);

  const canReply = t && (scope === 'admin' || t.status !== 'closed');

  return (
    <div className="bl-drawer-backdrop" onClick={onClose}>
      <aside className="bl-drawer sp-drawer" role="dialog" aria-modal="true" aria-labelledby="sp-drawer-title" onClick={(e) => e.stopPropagation()}>
        <header className="bl-drawer-head">
          <div className="sp-head-text">
            <div className="ft-mono bl-small bl-drawer-eyebrow">
              {t ? t.ref : '…'}{t?.site_name ? ` · ${t.site_name}` : ''}
            </div>
            <h2 id="sp-drawer-title" className="sp-drawer-title">{t ? t.subject : 'Loading…'}</h2>
            {t && (
              <div className="sp-head-tags">
                <TicketStatus status={t.status} scope={scope} />
                <PriorityTag priority={t.priority} />
                <span className="bl-muted bl-small">{t.category_label}</span>
              </div>
            )}
          </div>
          <button type="button" className="bl-icon-btn" onClick={onClose} aria-label="Close"><i className="fas fa-times"></i></button>
        </header>

        <div className="bl-drawer-body sp-drawer-body">
          {error && <div className="ft-toolbar-error" role="alert">{error}</div>}
          {!t && !error && <div className="bl-drawer-loading">Loading ticket…</div>}

          {t && scope === 'client' && (
            <>
              <ProgressTrack ticket={t} />
              {t.assigned_to_name && t.status !== 'new' && (
                <p className="sp-handler">Handled by {t.assigned_to_name}</p>
              )}
              {t.status === 'waiting_client' && (
                <p className="sp-callout">We need a reply from you to continue. Answer below.</p>
              )}
              {t.status === 'resolved' && (
                <div className="sp-callout sp-confirm">
                  <p>We believe this is fixed. Is it working now?</p>
                  <div className="sp-confirm-actions">
                    <button type="button" className="bl-btn-primary bl-btn-sm" disabled={saving}
                            onClick={() => apply(`${base}/tickets/${ticketId}/confirm`).catch(() => {})}>Yes, it’s fixed</button>
                    <button type="button" className="bl-btn-secondary bl-btn-sm" disabled={saving}
                            onClick={() => apply(`${base}/tickets/${ticketId}/reopen`).catch(() => {})}>Still not working</button>
                  </div>
                </div>
              )}
              {t.status === 'closed' && (
                <p className="sp-callout sp-callout-quiet">
                  This ticket is closed.{' '}
                  <button type="button" className="sp-linkbtn" disabled={saving}
                          onClick={() => apply(`${base}/tickets/${ticketId}/reopen`).catch(() => {})}>Reopen it</button>{' '}
                  if the problem is back.
                </p>
              )}
            </>
          )}

          {t && scope === 'admin' && (
            <>
              <div className="sp-controls">
                <label>
                  <span>Status</span>
                  <select className="bl-select" value={t.status} disabled={saving} onChange={(e) => adminPatch({ status: e.target.value })}>
                    {STATUS_ORDER.map((s) => <option key={s} value={s}>{statusLabel(s, 'admin')}</option>)}
                  </select>
                </label>
                <label>
                  <span>Assignee</span>
                  <select className="bl-select" value={t.assigned_to || ''} disabled={saving}
                          onChange={(e) => adminPatch({ assigned_to: e.target.value || null })}>
                    <option value="">Unassigned</option>
                    {staff.map((s) => <option key={s.username} value={s.username}>{s.name}{s.username === me ? ' (me)' : ''}</option>)}
                    {t.assigned_to && !staff.some((s) => s.username === t.assigned_to) && (
                      <option value={t.assigned_to}>{t.assigned_to_name || t.assigned_to}</option>
                    )}
                  </select>
                </label>
                <label>
                  <span>Priority</span>
                  <select className="bl-select" value={t.priority} disabled={saving} onChange={(e) => adminPatch({ priority: e.target.value })}>
                    {PRIORITIES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                  </select>
                </label>
                {me && t.assigned_to !== me && (
                  <button type="button" className="bl-btn-ghost is-accent sp-take" disabled={saving}
                          onClick={() => adminPatch({ assigned_to: me })}>Take it</button>
                )}
              </div>
              <dl className="bl-facts sp-facts">
                <div><dt>Client</dt><dd>{t.client_name || '—'}</dd></div>
                <div><dt>Vessel</dt><dd>{t.site_name || 'Whole account'}</dd></div>
                <div><dt>Raised by</dt><dd>{t.created_by_name || t.created_by}{t.created_by_email ? <span className="bl-muted bl-small"> · {t.created_by_email}</span> : null}</dd></div>
                <div><dt>Opened</dt><dd>{formatStamp(t.created_at)} <span className="bl-muted bl-small">({timeAgo(t.created_at)})</span></dd></div>
              </dl>
              <Snapshot snap={t.snapshot} />
            </>
          )}

          {t && (
            <ol className="sp-thread" aria-label="Conversation">
              {feed.map((it) => it.type === 'evt' ? (
                <li key={it.key} className="sp-evt">
                  <span>{eventText(it.e, scope)}</span>
                  <time className="bl-muted">{formatStamp(it.at)}</time>
                </li>
              ) : (
                <li key={it.key} className={`sp-msg is-${it.m.author_type} ${it.m.is_internal ? 'is-internal' : ''}`}>
                  <div className="sp-msg-head">
                    <span className="sp-msg-author">
                      {it.m.is_internal ? <><i className="fas fa-lock"></i> </> : null}
                      {it.m.author_name || (it.m.author_type === 'admin' ? 'Support team' : 'You')}
                    </span>
                    <time className="bl-muted" title={formatStamp(it.at)}>{timeAgo(it.at)}</time>
                  </div>
                  <div className="sp-msg-body">{it.m.body}</div>
                  <AttachmentStrip items={it.m.attachments} base={`${base}/attachments`} />
                </li>
              ))}
            </ol>
          )}
        </div>

        {canReply && (
          <Composer
            scope={scope}
            disabled={saving}
            onSend={send}
            placeholder={scope === 'admin' ? 'Reply to the client…' : t.status === 'resolved' ? 'Tell us what’s still wrong…' : 'Add details or answer a question…'}
          />
        )}
      </aside>
    </div>
  );
}
